import type { Customer, PosDataAdapter, Product, Receipt, SyncSnapshot } from "./types";

export const demoProducts: Product[] = [
  { id: "p1", name: "Flat White", category: "Coffee", price: 3.4, colour: "#ead8c5", emoji: "☕", stock: 42 },
  { id: "p2", name: "Cappuccino", category: "Coffee", price: 3.5, colour: "#dfc5ac", emoji: "☕", stock: 31 },
  { id: "p3", name: "Iced Latte", category: "Cold drinks", price: 3.85, colour: "#d8e8ea", emoji: "🧊", stock: 24 },
  { id: "p4", name: "Green Tea", category: "Tea", price: 2.75, colour: "#dce8d2", emoji: "🍵", stock: 18 },
  { id: "p5", name: "Croissant", category: "Bakery", price: 2.9, colour: "#f2dfb8", emoji: "🥐", stock: 12 },
  { id: "p6", name: "Cinnamon Roll", category: "Bakery", price: 3.25, colour: "#ead0b7", emoji: "🍥", stock: 8 },
  { id: "p7", name: "Avocado Toast", category: "Food", price: 7.5, colour: "#d9e4c6", emoji: "🥑", stock: 14 },
  { id: "p8", name: "Club Sandwich", category: "Food", price: 8.95, colour: "#e7d7bd", emoji: "🥪", stock: 10 },
  { id: "p9", name: "Orange Juice", category: "Cold drinks", price: 3.2, colour: "#f5d7a3", emoji: "🍊", stock: 20 },
  { id: "p10", name: "Sparkling Water", category: "Cold drinks", price: 2.2, colour: "#d6e9ef", emoji: "💧", stock: 36 },
  { id: "p11", name: "Blueberry Bowl", category: "Food", price: 6.8, colour: "#ded5eb", emoji: "🫐", stock: 7 },
  { id: "p12", name: "Chocolate Cake", category: "Bakery", price: 4.25, colour: "#dac4b6", emoji: "🍰", stock: 6 },
];

let receipts: Receipt[] = [
  { id: "r1", number: "#1048", createdAt: "Today, 10:42", total: 12.65, itemCount: 3, lines: [], payment: "Card", customer: "Jamie Cooper", syncState: "synced" },
  { id: "r2", number: "#1047", createdAt: "Today, 10:16", total: 7.3, itemCount: 2, lines: [], payment: "Cash", syncState: "synced" },
  { id: "r3", number: "#1046", createdAt: "Today, 09:51", total: 18.8, itemCount: 4, lines: [], payment: "Card", syncState: "pending" },
];
export const demoCustomers: Customer[] = [
  { id: "c1", name: "Jamie Cooper", email: "jamie@example.com", visits: 18, spend: 214.4 },
  { id: "c2", name: "Morgan Lee", email: "morgan@example.com", visits: 12, spend: 148.2 },
  { id: "c3", name: "Alex Morgan", email: "alex@example.com", visits: 7, spend: 82.5 },
];

let sync: SyncSnapshot = { online: typeof navigator === "undefined" || navigator.onLine, syncing: false, pendingChanges: 1, lastSyncedAt: "10:41" };
const listeners = new Set<(state: SyncSnapshot) => void>();
const emit = () => listeners.forEach((listener) => listener(sync));

export const demoAdapter: PosDataAdapter = {
  async listProducts() { return demoProducts; },
  async listReceipts() { return receipts; },
  async listCustomers() { return demoCustomers; },
  async checkout(input) {
    const total = input.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
    const receipt: Receipt = { id: crypto.randomUUID(), number: `#${1049 + receipts.length}`, createdAt: "Just now", total,
      subtotal: total / 1.2, tax: total - total / 1.2, itemCount: input.lines.reduce((sum, l) => sum + l.quantity, 0),
      lines: input.lines.map((line) => ({ productId: line.productId,
        name: demoProducts.find((product) => product.id === line.productId)?.name ?? "Item",
        quantity: line.quantity, unitPrice: line.unitPrice, total: line.quantity * line.unitPrice })),
      payment: input.paymentMethod === "card" ? "Card" : "Cash", syncState: sync.online ? "synced" : "pending" };
    receipts = [receipt, ...receipts];
    if (!sync.online) { sync = { ...sync, pendingChanges: sync.pendingChanges + 1 }; emit(); }
    return receipt;
  },
  async requestSync() {
    if (!sync.online || sync.syncing) return;
    sync = { ...sync, syncing: true }; emit();
    await new Promise((resolve) => setTimeout(resolve, 700));
    receipts = receipts.map((r) => ({ ...r, syncState: "synced" }));
    sync = { ...sync, syncing: false, pendingChanges: 0, lastSyncedAt: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }; emit();
  },
  subscribeSync(listener) {
    listeners.add(listener); listener(sync);
    const update = () => { sync = { ...sync, online: navigator.onLine }; emit(); if (sync.online && sync.pendingChanges) void demoAdapter.requestSync(); };
    window.addEventListener("online", update); window.addEventListener("offline", update);
    return () => { listeners.delete(listener); window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }
};
